import React, { useEffect, useMemo } from "react";
import { Button, Modal, ModalBody, Spinner } from "reactstrap";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import SimpleBar from "simplebar-react";
import "../../../../modules/Volunteer/MasterVolunteer/css/volunteer.module.scss";
import { useDispensingLog } from "modules/Pharmacy/Dispensing/api/useDispensingLog";
import { parseLocalDate } from "helpers/constant";

/* =========================================================
   Filter Value Interface
========================================================= */

export interface SubjectWiseFilterValues {
  subjectno?: string | null;
  ipcategory?: string | null;
  actionname?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
}

/* =========================================================
   Props
========================================================= */

interface Props {
  isOpen: boolean;
  toggle: () => void;
  onApply: (filters: SubjectWiseFilterValues) => void;
  onClear: () => void;
  existingFilters?: SubjectWiseFilterValues;
  activeStudyId: number | string | null;
}

/* =========================================================
   Static Options
========================================================= */

const IP_CATEGORY_OPTIONS = [
  { label: "Test Product", value: "Test Product" },
  { label: "Reference Product", value: "Reference Product" },
];

const ACTION_NAME_OPTIONS = [
  { label: "Dispense", value: "DISPENSE" },
  { label: "Return", value: "RETURN" },
];

/* =========================================================
   Component
========================================================= */

const SubjectWiseFilterModal: React.FC<Props> = ({
  isOpen,
  toggle,
  onApply,
  onClear,
  existingFilters,
  activeStudyId,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<SubjectWiseFilterValues>({
    defaultValues: existingFilters ?? {},
  });

  const { DropdownQuery } = useDispensingLog({ getStudyDropDown: true });
  const studyOptions = (DropdownQuery.data as any)?.data || [];

  const subjectOptions = useMemo(() => {
    if (!activeStudyId || studyOptions.length === 0) return [];
    const activeStudy = studyOptions.find((s: any) => String(s.studyid) === String(activeStudyId));
    return activeStudy?.subjectno?.map((sub: string) => ({ label: sub, value: sub })) || [];
  }, [studyOptions, activeStudyId]);

  /* ---------------- DATE FORMATTER ---------------- */
  const handleDateChange = (
    dates: Date[],
    onChange: (val: string | null) => void,
  ) => {
    if (dates && dates[0]) {
      const d = dates[0];
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      onChange(`${yyyy}-${mm}-${dd}`);
    } else {
      onChange(null);
    }
  };

  /* ---------------- REACT SELECT STYLES ---------------- */
  const customSelectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      backgroundColor: "#f3f3f3",
      borderColor: state.isFocused ? "#405189" : "transparent",
      borderRadius: "4px",
      fontSize: "12px",
      minHeight: "27px",
      height: "auto",
      boxShadow: "none",
      "&:hover": { borderColor: "#405189" },
    }),
    valueContainer: (base: any) => ({
      ...base,
      padding: "0 6px",
      fontSize: "12px",
    }),
    singleValue: (base: any) => ({
      ...base,
      color: "#495057",
    }),
    placeholder: (base: any) => ({
      ...base,
      color: "#adb5bd",
    }),
    input: (base: any) => ({
      ...base,
      margin: 0,
      padding: 0,
    }),
    indicatorsContainer: (base: any) => ({
      ...base,
      height: "25px",
    }),
    option: (base: any) => ({
      ...base,
      fontSize: "12px",
      padding: "6px 10px",
      minHeight: "25px",
      display: "flex",
      alignItems: "center",
    }),
    menu: (base: any) => ({
      ...base,
      zIndex: 9999,
    }),
    menuPortal: (base: any) => ({
      ...base,
      zIndex: 9999,
    }),
  };

  /* ---------------- APPLY FILTER ---------------- */

  const onSubmit = (values: SubjectWiseFilterValues) => {
    const normalizedFilters: SubjectWiseFilterValues = {
      subjectno: values.subjectno || null,
      ipcategory: values.ipcategory || null,
      actionname: values.actionname || null,
      fromDate: values.fromDate || null,
      toDate: values.toDate || null,
    };
    onApply(normalizedFilters);
    toggle();
  };

  /* ---------------- CLEAR FILTER ---------------- */

  const handleClear = () => {
    onClear();
    toggle();
    reset({
      subjectno: null,
      ipcategory: null,
      actionname: null,
      fromDate: null,
      toDate: null,
    });
  };

  useEffect(() => {
    if (existingFilters && Object.keys(existingFilters).length > 0) {
      reset(existingFilters);
    } else {
      reset({
        subjectno: null,
        ipcategory: null,
        actionname: null,
        fromDate: null,
        toDate: null,
      });
    }
  }, [existingFilters, reset]);

  /* ---------------- RENDER ---------------- */

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="lg"
      centered
      backdrop="static"
      className="volunteer-filter-modal common_modal"
    >
      <div style={{ fontFamily: '"hkgrotesk", "Poppins", sans-serif' }}>
        <div className="modal-header">
          <h5 className="modal-title-custom">Filter Subject Wise Report</h5>
          <p className="modal-subtitle">
            Apply specific criteria to narrow down your selection
          </p>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={toggle}
          ></button>
        </div>

        <ModalBody className="p-0">
          <SimpleBar className="p-4" style={{ maxHeight: "400px" }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="filter-section">
                <h6 className="section-title">REPORT FILTERS</h6>
                <div className="row">

                  {/* Subject No */}
                  <div className="col-md-6 mb-3">
                    <div className="form-group mb-0">
                      <label>SUBJECT NO</label>
                      <Controller
                        name="subjectno"
                        control={control}
                        render={({ field }) => (
                          <Select
                            options={subjectOptions}
                            styles={customSelectStyles}
                            placeholder={activeStudyId ? "Select Subject" : "Select Study Code First"}
                            isClearable
                            menuPortalTarget={document.body}
                            value={subjectOptions.find((opt: any) => opt.value === field.value) || null}
                            onChange={(val: any) => field.onChange(val ? val.value : null)}
                            disabled={!activeStudyId}
                          />
                        )}
                      />
                    </div>
                  </div>

                  {/* IP Category */}
                  <div className="col-md-6 mb-3">
                    <div className="form-group mb-0">
                      <label>IP CATEGORY</label>
                      <Controller
                        name="ipcategory"
                        control={control}
                        render={({ field }) => (
                          <Select
                            options={IP_CATEGORY_OPTIONS}
                            styles={customSelectStyles}
                            placeholder="Select IP Category"
                            isClearable
                            menuPortalTarget={document.body}
                            value={IP_CATEGORY_OPTIONS.find((opt: any) => opt.value === field.value) || null}
                            onChange={(val: any) => field.onChange(val ? val.value : null)}
                          />
                        )}
                      />
                    </div>
                  </div>

                  {/* Action Name */}
                  <div className="col-md-6 mb-3">
                    <div className="form-group mb-0">
                      <label>ACTION NAME</label>
                      <Controller
                        name="actionname"
                        control={control}
                        render={({ field }) => (
                          <Select
                            options={ACTION_NAME_OPTIONS}
                            styles={customSelectStyles}
                            placeholder="Select Action"
                            isClearable
                            menuPortalTarget={document.body}
                            value={ACTION_NAME_OPTIONS.find((opt: any) => opt.value === field.value) || null}
                            onChange={(val: any) => field.onChange(val ? val.value : null)}
                          />
                        )}
                      />
                    </div>
                  </div>

                  {/* Date Range */}
                  <div className="col-md-6 mb-3">
                    <div className="form-group mb-0">
                      <label>ACTION DATE RANGE</label>
                      <div className="range-inputs">
                        <Controller
                          name="fromDate"
                          control={control}
                          render={({ field }) => (
                            <Flatpickr
                              className="form-control"
                              options={{ dateFormat: "d/m/Y" }}
                              placeholder="dd/mm/yyyy"
                              value={field.value ? (parseLocalDate(field.value) ?? undefined) : undefined}
                              onChange={(dates: Date[]) => handleDateChange(dates, field.onChange)}
                            />
                          )}
                        />
                        <span className="range-separator">to</span>
                        <Controller
                          name="toDate"
                          control={control}
                          render={({ field }) => (
                            <Flatpickr
                              className="form-control"
                              options={{ dateFormat: "d/m/Y" }}
                              placeholder="dd/mm/yyyy"
                              value={field.value ? (parseLocalDate(field.value) ?? undefined) : undefined}
                              onChange={(dates: Date[]) => handleDateChange(dates, field.onChange)}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </form>
          </SimpleBar>
        </ModalBody>

        <div className="modal-footer-custom">
          <button type="button" className="btn-clear" onClick={handleClear}>
            <i className="ri-refresh-line"></i> Clear Filters
          </button>

          <div className="d-flex gap-2">
            <Button color="secondary" outline onClick={toggle}>
              Cancel
            </Button>
            <Button disabled={isSubmitting} onClick={handleSubmit(onSubmit)} outline color="primary">
              {isSubmitting && <Spinner size="sm" className="me-2" />}
              Apply Filters
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SubjectWiseFilterModal;
